'use client'

import * as React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { REGISTER_HREF } from '@/lib/copy/plans'

// Sticky mobile CTA. Slides up once the visitor has both scrolled past the
// hero AND spent a little time on the page, then offers the UGC audit (the
// mobile counterpart to the desktop LeadMagnetPopup, same /tools/ugc-audit
// destination). Spring slide-in; instant under reduced motion. Sits above
// the home-screen safe area. Desktop keeps the header CTAs + lead magnet.

export function StickyMobileCta() {
  const reduce = useReducedMotion()
  const pathname = usePathname()
  const [scrolled, setScrolled] = React.useState(false)
  const [dwelled, setDwelled] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 680)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    // Hold the bar back until the visitor has spent a little time on the
    // page, so it reads as an offer rather than an instant pop-up.
    const t = setTimeout(() => setDwelled(true), 10_000)
    return () => {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(t)
    }
  }, [])

  // Don't pitch signup to someone already on the register-adjacent tools.
  const onAuditPage = pathname?.startsWith('/tools/ugc-audit') ?? false
  const show = scrolled && dwelled && !onAuditPage

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {show && (
          <m.div
            className="fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 px-4 pb-[calc(0.7rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_-12px_rgba(13,20,36,0.25)] backdrop-blur lg:hidden"
            initial={reduce ? false : { y: '110%' }}
            animate={{ y: 0 }}
            exit={reduce ? { opacity: 0 } : { y: '110%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 34 }}
          >
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 text-sm font-semibold text-foreground">Start free. No card.</p>
              <Link
                href={REGISTER_HREF}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Start free <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  )
}
